import React, { useEffect } from "react"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  CardTitle,
  Label,
  Button,
  Form,
  Input,
  CardSubtitle,
  Table,
  InputGroup,
} from "reactstrap"
import { addFrameMaster } from "service/api"
import Select from 'react-select';

import DatatableTables from "./FramesStocks";




const defaultValue = {
  frame_code: "",
  frame_name: "",
  frame_color: "",
  frame_size: "",
  price: "",
  frame_material: "",
  frame_type: "",
  frame_shape: "",
};

const AddFrame = () => {
  const [frame, setFrame] = useState(defaultValue);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Frames | Add Frames";
  }, []);

  const onValueChange = (e) => {
    const { name, value } = e.target;
    setFrame((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const buildFrameCode = () => {
    const normalizePart = (value, fallback = "NA") => {
      const normalized = (value || "")
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "");

      return normalized ? normalized.slice(0, 3) : fallback;
    };

    return [
      normalizePart(frame.frame_name),
      normalizePart(frame.frame_color),
      normalizePart(frame.frame_size),
      normalizePart(frame.frame_material),
    ].join("-");
  };

  const generateTitle = () => {
    const { frame_name, frame_size, frame_color, frame_material } = frame;
    return `${frame_name} ${frame_size} ${frame_color} ${frame_material}`;
  };

  useEffect(() => {
    setFrame((prev) => {
      const generatedFrameCode = buildFrameCode();

      if (prev.frame_code === generatedFrameCode) {
        return prev;
      }

      return { ...prev, frame_code: generatedFrameCode };
    });
  }, [frame.frame_name, frame.frame_color, frame.frame_size, frame.frame_material]);

  const validate = () => {
    const newErrors = {};
    if (!frame.frame_name.trim()) newErrors.frame_name = "Frame name is required";
    if (!frame.frame_color.trim()) newErrors.frame_color = "Color is required";
    if (!frame.frame_size.trim()) newErrors.frame_size = "Size is required";
    if (!frame.frame_material.trim()) newErrors.frame_material = "Material is required";
    if (!frame.frame_type.trim()) newErrors.frame_type = "Type is required";
    if (!frame.frame_shape.trim()) newErrors.frame_shape = "Shape is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddFrame = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    const payload = {
      ...frame,
      frame_code: frame.frame_code || buildFrameCode(),
      frame_title: generateTitle(),
      frame_brand: null,
    };

    try {
      await addFrameMaster(payload);
      toast.success("Frame added successfully", { position: "top-center", autoClose: 1500 });
      setFrame(defaultValue);
      navigate("/FrameMaster");
    } catch (error) {
      console.error("Add error:", error);
      toast.error("Failed to add frame");
    } finally {
      setLoading(false);
    }
  };


  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h4 className="page-title"> Add Frames </h4>
            <Link to="/FrameMaster" className="btn btn-danger">Frames</Link>
          </div>
          <Row>
            <Card>
              <CardBody>
                <Form>
                  <Row>
                    <Col lg={3}>
                      <div className="mb-3">
                        <Label htmlFor="frame_code">Frame Code</Label>
                        <Input
                          type="text"
                          name="frame_code"
                          placeholder="Generated Frame Code"
                          value={frame.frame_code}
                          readOnly
                        />
                      </div>
                    </Col>

                    <Col lg={3}>
                      <div className="mb-3">
                        <Label htmlFor="frame_name">Frame Name<span style={{ color: "red" }}>*</span></Label>
                        <Input
                          type="text"
                          name="frame_name"
                          placeholder="Enter Frame Name"
                          value={frame.frame_name}
                          onChange={onValueChange}
                          invalid={!!errors.frame_name}
                        />
                        {errors.frame_name && (
                          <div className="invalid-feedback d-block">{errors.frame_name}</div>
                        )}
                      </div>
                    </Col>

                    <Col md={3}>
                      <div className="mb-3">
                        <Label htmlFor="frame_color">Color<span style={{ color: "red" }}>*</span></Label>
                        <Input
                          type="text"
                          name="frame_color"
                          value={frame.frame_color}
                          onChange={onValueChange}
                          invalid={!!errors.frame_color}
                        />
                        {errors.frame_color && (
                          <div className="invalid-feedback d-block">{errors.frame_color}</div>
                        )}

                      </div>
                    </Col>

                    <Col lg={3}>
                      <div className="mb-3">
                        <Label htmlFor="frame_size">Size<span style={{ color: "red" }}>*</span></Label>
                        <Input
                          type="text"
                          name="frame_size"
                          placeholder="Enter Size"
                          value={frame.frame_size}
                          onChange={onValueChange}
                          invalid={!!errors.frame_size}
                        />
                        {errors.frame_size && (
                          <div className="invalid-feedback d-block">{errors.frame_size}</div>
                        )}

                      </div>
                    </Col>

                    <Col lg={3}>
                      <div className="mb-3">
                        <Label htmlFor="price">Price</Label>
                        <Input
                          type="number"
                          name="price"
                          placeholder="Enter Price"
                          value={frame.price}
                          onChange={onValueChange}
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </Col>

                    {/* Material Radio */}
                    <Col lg={3}>
                      <div className="mb-3">
                        <Label className="form-label">Material<span style={{ color: "red" }}>*</span></Label>
                        <div className="d-flex flex-wrap gap-2">
                          {["Acetate", "Titanium", "Nylon"].map((material, index) => (
                            <div className="form-check" key={index}>
                              <input
                                className="form-check-input"
                                type="radio"
                                name="frame_material"
                                id={`material_${material.toLowerCase()}`}
                                value={material}
                                onChange={onValueChange}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`material_${material.toLowerCase()}`}
                              >
                                {material}
                              </label>
                            </div>
                          ))}
                        </div>
                        {errors.frame_material && (
                          <div className="text-danger mt-1">{errors.frame_material}</div>
                        )}
                      </div>
                    </Col>

                    {/* Type Radio */}
                    <Col lg={3}>
                      <div className="mb-3">
                        <Label className="form-label">Type</Label>
                        <div className="d-flex flex-wrap gap-2">
                          {["Full Rim", "Semi-rimless", "Rimless"].map((type, index) => (
                            <div className="form-check" key={index}>
                              <input
                                className="form-check-input"
                                type="radio"
                                name="frame_type"
                                id={`type_${type.toLowerCase().replace(/\s/g, "_")}`}
                                value={type}
                                onChange={onValueChange}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`type_${type.toLowerCase().replace(/\s/g, "_")}`}
                              >
                                {type}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>

                    {/* Shape Radio */}
                    <Col lg={3}>
                      <div className="mb-3">
                        <Label className="form-label">Shape</Label>
                        <div className="d-flex flex-wrap gap-2">
                          {["Rectangle", "cat-eye", "Round", "Oval"].map((shape, index) => (
                            <div className="form-check" key={index}>
                              <input
                                className="form-check-input"
                                type="radio"
                                name="frame_shape"
                                id={`shape_${shape.toLowerCase().replace(/\s/g, "_")}`}
                                value={shape}
                                onChange={onValueChange}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={`shape_${shape.toLowerCase().replace(/\s/g, "_")}`}
                              >
                                {shape}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>


                  </Row>

                  <Button
                    color="primary"
                    className="w-md mx-2"
                    onClick={handleAddFrame}
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save"}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Row>


        </Container>
      </div>
    </React.Fragment>
  );

}

export default AddFrame;
